import React, { useEffect, useMemo, useState } from "react";
import usePulseLocale from "./i18n";
import { tokens, fmtNum, fmtPeriod } from "./theme";
import { SkeletonCard, NoData, SectionTitle, BreakBadge, breakNote } from "./components";
import ItalyMap from "./ItalyMap";
import VulnerabilityTable from "./VulnerabilityTable";
import { regionName } from "./regionNames";
import { pulseGet } from "./api";

export const COMPONENTS = ["pressure", "seasonality", "concentration", "air", "platform"];

const Pill = ({ active, onClick, children, dm, testid, disabled, title }) => {
  const T = tokens(dm);
  return (
    <button onClick={onClick} aria-pressed={active} data-testid={testid} disabled={disabled} title={title}
      className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 whitespace-nowrap disabled:cursor-not-allowed"
      style={{ background: active ? T.brand : T.surface, color: active ? "#fff" : T.muted, border: `1px solid ${active ? T.brand : T.border}`,
               opacity: disabled ? 0.45 : 1, textDecoration: disabled ? "line-through" : "none" }}>
      {children}
    </button>
  );
};

function toCsv(data, lang) {
  const head = ["rank", "code", "region", "composite", "components_available", "components_total",
    ...COMPONENTS.map((c) => `${c}_score`), ...COMPONENTS.map((c) => `${c}_weight_effective`)];
  const rows = data.regions.map((r) => [r.rank ?? "", r.code, regionName(r.code, lang, r.label), r.composite ?? "",
    r.components_available ?? "", r.components_total ?? COMPONENTS.length,
    ...COMPONENTS.map((c) => r.components[c]?.score ?? ""), ...COMPONENTS.map((c) => r.weights_effective?.[c] ?? "")]);
  const m = data.methodology || {};
  const meta = [`# ${m.rescaling_note || ""}`, `# recomputed_at=${m.recomputed_at || ""} warehouse_version=${m.warehouse_version ?? ""}`,
    `# included_since_concentration=${m.included_since?.concentration || ""}`, "# empty cell = component not available (null), never 0"];
  return [...meta, ...[head, ...rows].map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
}

/** Regional tourism vulnerability index — choropleth + ranked table + methodology (weights, latest data, perimeter). */
export default function ItalyVulnerabilityPanel({ dm }) {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("composite");
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    let on = true;
    setLoading(true);
    pulseGet("italy/vulnerability", { lang }).then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData(null); }).finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [lang]);

  const mapValues = useMemo(() => {
    const out = {};
    (data?.regions || []).forEach((r) => { out[r.code] = metric === "composite" ? r.composite : r.components[metric]?.score ?? null; });
    return out;
  }, [data, metric]);
  const emptyComponents = useMemo(() => new Set(COMPONENTS.filter((c) => (data?.regions || []).every((r) => r.components?.[c]?.score == null))), [data]);

  const download = () => {
    const blob = new Blob([toCsv(data, lang)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `italy_vulnerability_${data.year}.csv`; a.click();
    URL.revokeObjectURL(a.href);
  };

  const m = data?.methodology;
  const latest = m?.latest_period || {};
  const latestLabel = (c) => {
    const v = latest[c];
    if (!v) return "—";
    if (typeof v === "object") return `${v.nights} · pop ${v.population} · km² ${v.area}`;
    return /^\d{4}-\d{2}$/.test(v) ? fmtPeriod(v, lang) : v;
  };

  return (
    <section className="mb-10" data-testid="italy-vulnerability-panel" aria-labelledby="italy-vuln-title">
      <SectionTitle dm={dm} right={
        <div className="flex items-center gap-1 flex-wrap" role="group" aria-label={t("vuln_metric_switch")}>
          <Pill dm={dm} active={metric === "composite"} onClick={() => setMetric("composite")} testid="vuln-metric-composite">{t("vuln_composite")}</Pill>
          {COMPONENTS.map((c) => (
            <Pill key={c} dm={dm} active={metric === c} disabled={emptyComponents.has(c)} onClick={() => setMetric(c)} testid={`vuln-metric-${c}`}
              title={emptyComponents.has(c) ? t("vuln_component_na") : undefined}>{t(`vuln_${c}`)}{emptyComponents.has(c) ? " · n.d." : ""}</Pill>
          ))}
        </div>
      }>
        <span id="italy-vuln-title">{t("vuln_title", { year: data?.year || "" })}</span>
      </SectionTitle>
      <p className="text-xs mb-4 max-w-3xl" style={{ color: T.muted }}>{t("vuln_sub")}</p>

      {loading && <SkeletonCard h={420} dm={dm} />}
      {!loading && !data?.regions?.length && <NoData dm={dm} h={200} testid="vuln-no-data" />}
      {!loading && data?.regions?.length > 0 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="vuln-map">
              <ItalyMap values={mapValues} format={(v) => fmtNum(v, lang, 1)} diverging={false} dm={dm} lang={lang}
                selected={selected} hovered={hovered} onSelect={(c) => setSelected(selected === c ? null : c)} onHover={setHovered}
                noValueHint={t("no_data")} srCaption={`${t("vuln_title", { year: data.year })} — ${metric === "composite" ? t("vuln_composite") : t(`vuln_${metric}`)}`} />
              <p className="text-[10px] mt-2" style={{ color: T.muted }}>{t("vuln_scale_hint")}</p>
            </div>
            <div className="lg:col-span-3">
              <VulnerabilityTable data={data} dm={dm} selected={selected} onSelect={(c) => setSelected(selected === c ? null : c)} metric={metric} />
            </div>
          </div>

          <div className="mt-4 p-4 text-xs" style={{ background: T.surface2, border: `1px solid ${T.border}` }} data-testid="vuln-methodology">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.muted }}>{t("vuln_method_title")}</span>
              <div className="flex items-center gap-2 flex-wrap">
                {m?.series_break && <BreakBadge brk={m.series_break} dm={dm} testid="vuln-break-badge" />}
                <button onClick={download} data-testid="vuln-csv" className="text-[10px] font-bold uppercase tracking-wider px-2 py-1"
                  style={{ border: `1px solid ${T.border}`, color: T.text, background: T.surface }}>{t("vuln_csv")}</button>
              </div>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="vuln-weights">
              {COMPONENTS.map((c) => (
                <li key={c} className="p-2" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold">{t(`vuln_${c}`)}</span>
                    <span className="pulse-num text-base font-bold" style={{ color: T.accent }} data-testid={`vuln-weight-${c}`}>{m.weights[c]}%</span>
                  </div>
                  <div className="text-[10px] mt-1" style={{ color: T.muted }}>{t(`vuln_${c}_how`)}</div>
                  <div className="text-[10px] mt-1 pulse-num" style={{ color: T.muted }} data-testid={`vuln-latest-${c}`}>{t("vuln_latest")}: {latestLabel(c)}</div>
                  <div className="text-[10px] mt-1" style={{ color: T.muted }}>{(m.sources?.[c] || []).join(" · ")}</div>
                </li>
              ))}
            </ul>
            <p className="mt-2" style={{ color: T.muted }}>{t("vuln_norm")}</p>
            <p className="mt-1 font-semibold" style={{ color: m.missing_components?.length ? (dm ? "#f3cf6a" : "#8a5a00") : T.muted }} data-testid="vuln-rescaling-note">{m.rescaling_note}</p>
            <p className="mt-1 pulse-num" style={{ color: T.muted }} data-testid="vuln-recomputed">
              {t("vuln_recomputed")}: {m.recomputed_at ? new Date(m.recomputed_at).toLocaleString(lang === "it" ? "it-IT" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}
              {m.warehouse_version ? ` · warehouse v${m.warehouse_version}` : ""}
              {" · "}{t("vuln_included_since")}: {m.included_since?.concentration ? new Date(m.included_since.concentration).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { dateStyle: "medium" }) : t("vuln_not_yet_included")}
            </p>
            {m?.series_break && <p className="mt-1" style={{ color: T.muted }} data-testid="vuln-break-note">{breakNote(m.series_break, lang)} {t("vuln_break_ranking")}</p>}
            <ul className="mt-1 list-disc pl-4" style={{ color: T.muted }}>
              {(m.notes || []).map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        </>
      )}
    </section>
  );
}

